import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import LayoutContext from "./LayoutContext";
import { NewHeader, NewFooter, MenuBar } from "@components";
import Sidebar from "@components/Sidebar";
import { menuBarList } from "./DefaultLayoutType";
import {
  getClosedFeatureRedirectPath,
  isClosedFeatureRoute,
  resolveShiftScheduleMenuPagination,
} from "@helpers/checkPermission";
import { info } from "@alert";

/** 視窗寬度不大於此值時，左側子選單（MenuBar）自動收合，避免擠壓主內容 */
const SUBMENU_AUTO_COLLAPSE_MAX_WIDTH_PX = 1400;

/** 僅此類根路徑由 DefaultLayout 處理「本功能未開放」，避免與 Statistics 等子 Layout 重複彈窗 */
function shouldDefaultLayoutHandleClosedFeature(pathname) {
  const root = pathname.split("/").filter(Boolean)[0];
  return root === "test" || root === "overview";
}

const DefaultLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = useSelector((state) => state.user.user.permissions);
  const lastClosedDeniedPath = useRef(null);
  const path = location.pathname;
  /** 僅帳務：主內容欄需高於 .new-header (z-40)。請採驗不可加——整條 d-flex 抬升會讓捲動中的導覽蓋住右上角分店（非橡皮圈也會發生） */
  const outletStackOverHeader = path.startsWith("/accounting");
  const pathTrim = path.replace(/\/+$/, "") || "/";
  const pimsNavbarOverGlobalHeader =
    path.split("/")[1] === "pims" &&
    (pathTrim === "/pims/vendors" || pathTrim === "/pims/itemPrices");
  const routerName = path.split("/")[1];
  const pagination = path.split("/")[2];
  const menuPagination =
    routerName === "shiftschedule"
      ? resolveShiftScheduleMenuPagination(pagination)
      : pagination;
  const subPage = path.split("/")[3];
  const [sidebarModal, setSidebarModal] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > SUBMENU_AUTO_COLLAPSE_MAX_WIDTH_PX;
  });
  const [renderBar, setRenderBar] = useState("");
  const [modalProp, setModalProp] = useState({});

  useEffect(() => {
    const onResize = () => {
      setSidebarModal(window.innerWidth > SUBMENU_AUTO_COLLAPSE_MAX_WIDTH_PX);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!shouldDefaultLayoutHandleClosedFeature(location.pathname)) return;
    if (!isClosedFeatureRoute(location.pathname)) return;
    if (lastClosedDeniedPath.current === location.pathname) return;
    lastClosedDeniedPath.current = location.pathname;
    info(1, 
      "本功能未開放", 
      "", 
      "我了解了"
    );
    navigate(getClosedFeatureRedirectPath(location.pathname, permissions), {
      replace: true,
    });
  }, [location.pathname, location.key, permissions, navigate]);

  const blockClosedOutlet =
    shouldDefaultLayoutHandleClosedFeature(path) && isClosedFeatureRoute(path);

  const childrenToRender = React.Children.map(children, (child) => {
    if (blockClosedOutlet && child?.type === Outlet) {
      return null;
    }
    return child;
  });

  return (
    <Fragment>
      <LayoutContext.Provider
        value={{ renderBar, setRenderBar, modalProp, setModalProp }}
      >
        <div className="h-100">
          {/* <Header /> */}
          <NewHeader
            renderBar={renderBar}
            pagination={pagination}
            subPage={subPage}
            sidebarModal={sidebarModal}
            menuBarList={menuBarList}
            routerName={routerName}
          />
          <div
            className={`d-flex h-100${outletStackOverHeader ? " layout-flex--stack-over-header" : ""}${
              routerName === "pims" ? " layout-flex--pims" : ""
            }`}
          >
            {!menuBarList[routerName] || (
              <MenuBar
                sidebarModal={sidebarModal}
                setSidebarModal={setSidebarModal}
                menuBarList={menuBarList[routerName]}
                routerName={routerName}
                pagination={menuPagination}
              />
            )}
            <div
              className={`w-100 ${routerName === "pims" ? "layout-main--pims" : ""}${
                pimsNavbarOverGlobalHeader ? " layout-main--pims-navbar-over-global" : ""
              }${outletStackOverHeader ? " layout-main--stack-over-header" : ""}`}
              style={{
                transition: "padding-left 0.5s ease 0s",
                paddingLeft: `${
                  !menuBarList[routerName]
                    ? "0px"
                    : sidebarModal
                    ? "260px"
                    : "0px"
                }`,
              }}
            >
              {childrenToRender}
            </div>
          </div>
        </div>
        <Sidebar routerName={routerName} />
        <div className="voidFlag">已成功作廢發票</div>{" "}
        <div className="blackCover"></div>
        {/* 畫面的彈窗還是要往外放才行... */}
        {/* <NewFooter /> */}
      </LayoutContext.Provider>
    </Fragment>
  );
};

export default DefaultLayout;
